import { AlertsByAlarmReasonComponent } from "./../../alerts-by-alarm-reason/alerts-by-alarm-reason.component";
import { ResponseTimeHistogramComponent } from "../../response-time-histogram/response-time-histogram.component";
import { Subscription, combineLatest } from "rxjs";
import { StatisticsService } from "app/services/statistics.service";
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { IEmaacStatistics } from "app/models/statistics.model";
import { AlarmService } from "app/services/alarm.service";
import { IAlarmReasonTranslations } from "app/models/alarm-reasons.model";
import { HelperService, UiLang } from "app/services/helper.service";
import { CareRecipientCounterComponent } from "../../care-recipient-counter/care-recipient-counter.component";
import { EmaacAlertsComponent } from "../../emaac-alerts/emaac-alerts.component";
import { ResponseTimeStatisticsComponent } from "../../response-time-statistics/response-time-statistics.component";
import { AlertsPerDayComponent } from "../../alerts-per-day/alerts-per-day.component";
import { AlertsPerHourComponent } from "../../alerts-per-hour/alerts-per-hour.component";
import { AlertResponseTimesComponent } from "../../alert-response-times/alert-response-times.component";
import { TranslateModule } from "@ngx-translate/core";
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  StatisticsExportSnapshot,
  StatisticsExportWidget,
  StatisticsKpi,
  StatisticsTabExportSource,
  StatisticsExportProgressCallback,
} from "app/services/statistics-export.models";

@Component({
    selector: "alarm-center-tab",
    templateUrl: "./alarm-center-tab.component.html",
    styleUrls: ["./alarm-center-tab.component.css"],
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [
    TranslateModule,
    CareRecipientCounterComponent,
    EmaacAlertsComponent,
    ResponseTimeStatisticsComponent,
    AlertsByAlarmReasonComponent,
    AlertsPerDayComponent,
    AlertsPerHourComponent,
    AlertResponseTimesComponent,
    ResponseTimeHistogramComponent,
    MatProgressBarModule,
  ],
})
export class AlarmCenterTabComponent implements OnInit, OnDestroy, StatisticsTabExportSource {
  constructor(
    public statistics: StatisticsService,
    private alarmService: AlarmService,
    private helper: HelperService
  ) { }

  emaacStatisticsData: IEmaacStatistics;
  alarmReasonTranslations: IAlarmReasonTranslations;
  subscription: Subscription;

  totalAlerts: number = 0;
  finishedAlerts: number = 0;
  emaAlerts: number = 0;
  careRecipientCount: number = 0;
  alertsPerDay: Record<string, number[]> = {};
  alertsPerHour: Record<string, number[]> = {};
  alertsByAlarmReasons: Record<string, Record<string, number>> = {};
  alarmCategoryTotalAlerts: Record<string, number> = {};
  mergedAlertResponses: IEmaacStatistics['responseTimes']['alertResponses'] = [];

  @ViewChild(CareRecipientCounterComponent) careRecipientCounter: CareRecipientCounterComponent;
  @ViewChild(EmaacAlertsComponent) emaacAlerts: EmaacAlertsComponent;
  @ViewChild(ResponseTimeStatisticsComponent) responseTimeStatistics: ResponseTimeStatisticsComponent;
  @ViewChild(AlertsByAlarmReasonComponent) alertsByAlarmReason: AlertsByAlarmReasonComponent;
  @ViewChild(AlertsPerDayComponent) alertsPerDayWidget: AlertsPerDayComponent;
  @ViewChild(AlertsPerHourComponent) alertsPerHourWidget: AlertsPerHourComponent;
  @ViewChild(AlertResponseTimesComponent) alertResponseTimes: AlertResponseTimesComponent;
  @ViewChild(ResponseTimeHistogramComponent) responseTimeHistogram: ResponseTimeHistogramComponent;

  ngOnInit() {
    const language = localStorage.getItem("language") as UiLang;

    this.totalAlerts = 0;
    this.finishedAlerts = 0;
    this.emaAlerts = 0;
    this.careRecipientCount = 0;
    this.alertsByAlarmReasons = {};
    this.alarmCategoryTotalAlerts = {};
    this.alertsPerDay = {};
    this.alertsPerHour = {};
    this.mergedAlertResponses = [];

    this.statistics.initPageCounter();

    this.subscription = combineLatest([
      this.statistics.emaacStatistics$,
      this.alarmService.getAlarmReasonTranslations(language),
    ]).subscribe(([emaacStatistics, translations]) => {
      if (translations) {
        this.alarmReasonTranslations = translations;
        this.statistics.alarmReasonTranslations = translations;
      }

      if (!emaacStatistics) {
        return;
      }

      this.statistics.alarmCenterTabDataLoaded = true;

      if (this.statistics.page === 1) {
        this.emaacStatisticsData = structuredClone(emaacStatistics);
        this.totalAlerts = emaacStatistics.emaacAlertStats.totalAlerts;
        this.finishedAlerts = emaacStatistics.emaacAlertStats.finishedAlerts;
        this.emaAlerts = emaacStatistics.emaacAlertStats.emaAlerts;
        this.careRecipientCount = emaacStatistics.careRecipientCount;
        this.mergedAlertResponses = [...(emaacStatistics.responseTimes?.alertResponses ?? [])];
        this.seedDayHourAggregates(emaacStatistics);
        this.mergeAlarmReasons(emaacStatistics);
      } else {
        this.totalAlerts += emaacStatistics.emaacAlertStats.totalAlerts;
        this.finishedAlerts += emaacStatistics.emaacAlertStats.finishedAlerts;
        this.emaAlerts += emaacStatistics.emaacAlertStats.emaAlerts;
        this.careRecipientCount = Math.max(this.careRecipientCount, emaacStatistics.careRecipientCount);
        this.mergedAlertResponses.push(...(emaacStatistics.responseTimes?.alertResponses ?? []));
        this.mergeDayHourPage(emaacStatistics);
        this.mergeAlarmReasons(emaacStatistics);
      }

      const pageTotal = emaacStatistics.emaacAlertStats.totalAlerts;
      if (pageTotal > 0 && pageTotal % this.statistics.pageSize === 0) {
        this.statistics.fetchEmaacStatistics$.next();
      } else {
        this.finalizeEmaacMerge();
      }
    }, (err) => { console.log(err); this.statistics.alarmCenterTabDataLoaded = true; this.finalizeEmaacMerge(); });

    this.statistics.fetchEmaacStatistics$.next();
  }

  private seedDayHourAggregates(page: IEmaacStatistics): void {
    for (let i = 0; i < page.alertsByDays.length; i++) {
      const deviceType = page.alertsByDays[i].deviceType;
      this.alertsPerDay[deviceType] = [...page.alertsByDays[i].alertsPerDay];
      this.alertsPerHour[deviceType] = [...page.alertsByHours[i].alertsPerHour];
    }
  }

  private mergeDayHourPage(page: IEmaacStatistics): void {
    for (let i = 0; i < page.alertsByDays.length; i++) {
      const deviceType = page.alertsByDays[i].deviceType;
      if (!this.alertsPerDay[deviceType]) {
        this.alertsPerDay[deviceType] = Array(7).fill(0);
      }
      if (!this.alertsPerHour[deviceType]) {
        this.alertsPerHour[deviceType] = Array(24).fill(0);
      }
      for (let d = 0; d < 7; d++) {
        this.alertsPerDay[deviceType][d] += page.alertsByDays[i].alertsPerDay[d];
      }
      for (let h = 0; h < 24; h++) {
        this.alertsPerHour[deviceType][h] += page.alertsByHours[i].alertsPerHour[h];
      }

      const dayEntry = this.emaacStatisticsData.alertsByDays.find((e) => e.deviceType === deviceType);
      if (dayEntry) {
        dayEntry.alertsPerDay = [...this.alertsPerDay[deviceType]];
      } else {
        this.emaacStatisticsData.alertsByDays.push({
          deviceType,
          alertsPerDay: [...this.alertsPerDay[deviceType]],
        });
      }
      const hourEntry = this.emaacStatisticsData.alertsByHours.find((e) => e.deviceType === deviceType);
      if (hourEntry) {
        hourEntry.alertsPerHour = [...this.alertsPerHour[deviceType]];
      } else {
        this.emaacStatisticsData.alertsByHours.push({
          deviceType,
          alertsPerHour: [...this.alertsPerHour[deviceType]],
        });
      }
    }
  }

  private mergeAlarmReasons(page: IEmaacStatistics): void {
    for (const categoryBlock of page.alertsByAlarmReasons) {
      const alarmReasonCategory = categoryBlock.alarmReasonCategory;
      if (!this.alertsByAlarmReasons[alarmReasonCategory]) {
        this.alarmCategoryTotalAlerts[alarmReasonCategory] = 0;
        this.alertsByAlarmReasons[alarmReasonCategory] = {};
      }
      for (const reasonAlert of categoryBlock.alarmReasonAlerts) {
        const alarmReason = reasonAlert.alarmReason;
        if (!this.alertsByAlarmReasons[alarmReasonCategory][alarmReason]) {
          this.alertsByAlarmReasons[alarmReasonCategory][alarmReason] = 0;
        }
        this.alertsByAlarmReasons[alarmReasonCategory][alarmReason] += reasonAlert.alertCount;
        this.alarmCategoryTotalAlerts[alarmReasonCategory] += reasonAlert.alertCount;

        let existingCategory = this.emaacStatisticsData.alertsByAlarmReasons.find(
          (c) => c.alarmReasonCategory === alarmReasonCategory
        );
        if (!existingCategory) {
          existingCategory = { alarmReasonCategory, totalAlerts: 0, alarmReasonAlerts: [] };
          this.emaacStatisticsData.alertsByAlarmReasons.push(existingCategory);
        }
        const existingReason = existingCategory.alarmReasonAlerts.find(
          (r) => r.alarmReason === alarmReason
        );
        if (existingReason) {
          existingReason.alertCount = this.alertsByAlarmReasons[alarmReasonCategory][alarmReason];
        } else {
          existingCategory.alarmReasonAlerts.push({
            alarmReason,
            alertCount: this.alertsByAlarmReasons[alarmReasonCategory][alarmReason],
          });
        }
        existingCategory.totalAlerts = this.alarmCategoryTotalAlerts[alarmReasonCategory];
      }
    }
  }

  private finalizeEmaacMerge(): void {
    if (!this.emaacStatisticsData) {
      this.statistics.alarmCenterTabPagingComplete = true;
      this.statistics.initPageCounter();
      return;
    }

    this.emaacStatisticsData.emaacAlertStats.totalAlerts = this.totalAlerts;
    this.emaacStatisticsData.emaacAlertStats.finishedAlerts = this.finishedAlerts;
    this.emaacStatisticsData.emaacAlertStats.emaAlerts = this.emaAlerts;
    this.emaacStatisticsData.careRecipientCount = this.careRecipientCount;
    this.emaacStatisticsData.responseTimes.alertResponses = this.mergedAlertResponses;
    this.emaacStatisticsData.responseTimes.resTimeStats =
      this.statistics.calculateResponseTimes(this.mergedAlertResponses);

    this.statistics.emaacStatistics = structuredClone(this.emaacStatisticsData);
    this.statistics.alarmCenterTabPagingComplete = true;
    this.statistics.initPageCounter();

    this.totalAlerts = 0;
    this.finishedAlerts = 0;
    this.emaAlerts = 0;
    this.alertsPerDay = {};
    this.alertsPerHour = {};
    this.alertsByAlarmReasons = {};
    this.alarmCategoryTotalAlerts = {};
    this.mergedAlertResponses = [];
  }

  private refreshCharts(): void {
    this.emaacAlerts?.refreshChart();
    this.alertsByAlarmReason?.refreshChart();
  }

  restoreViewAfterExport(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.refreshCharts();
      });
    });
  }

  async collectExportSnapshots(
    onProgress?: StatisticsExportProgressCallback
  ): Promise<StatisticsExportSnapshot[]> {
    const widgetEntries: Array<{ widget: StatisticsExportWidget | undefined; titleKey: string }> = [
      { widget: this.careRecipientCounter, titleKey: 'CARE_RECIPIENTS' },
      { widget: this.emaacAlerts, titleKey: 'ALERTS_WITH_FIRST_LETTER_UPPERCASE' },
      { widget: this.responseTimeStatistics, titleKey: 'RESPONSE_TIME' },
      { widget: this.alertsByAlarmReason, titleKey: 'ALERT_REASONS' },
      { widget: this.alertsPerDayWidget, titleKey: 'ALERTS_PER_DAY' },
      { widget: this.alertsPerHourWidget, titleKey: 'ALERTS_PER_HOUR' },
      { widget: this.responseTimeHistogram, titleKey: 'RESPONSE_TIME_DISTRIBUTION' },
      { widget: this.alertResponseTimes, titleKey: 'RESPONSE_TIME_PER_ALERT' },
    ];
    const snapshots: StatisticsExportSnapshot[] = [];
    const total = widgetEntries.length;

    for (let index = 0; index < widgetEntries.length; index++) {
      const { widget, titleKey } = widgetEntries[index];
      if (!widget) {
        throw new Error(
          `PDF export failed: widget "${titleKey}" is not available (view not rendered yet)`
        );
      }
      onProgress?.({
        phase: 'capture',
        current: index + 1,
        total,
        messageKey: titleKey,
      });
      try {
        const snapshot = await widget.captureForExport();
        // A widget with no data for the selected range returns null — skip it
        // instead of failing the whole export.
        if (snapshot) {
          snapshots.push(snapshot);
        }
      } catch (err) {
        throw new Error(`PDF export failed while capturing "${titleKey}": ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    return snapshots;
  }

  getExportKpis(): StatisticsKpi[] {
    if (!this.emaacStatisticsData) {
      return [];
    }
    const stats = this.emaacStatisticsData.emaacAlertStats;
    const resTime = this.emaacStatisticsData.responseTimes.resTimeStats;
    return [
      {
        labelKey: 'ALERTS_WITH_FIRST_LETTER_UPPERCASE',
        value: String(stats.totalAlerts),
      },
      {
        labelKey: 'FINISHED_BY_ALARM_CENTER',
        value: String(stats.finishedAlerts),
      },
      {
        labelKey: 'SENT_TO_EMA',
        value: String(stats.emaAlerts),
      },
      {
        labelKey: 'CARE_RECIPIENTS',
        value: String(this.emaacStatisticsData.careRecipientCount),
      },
      {
        labelKey: 'MEAN_TEXT',
        value: this.helper.convertSecondsToReadableFormat(resTime.avgResTimeSeconds),
      },
      {
        labelKey: 'MEDIAN',
        value: this.helper.convertSecondsToReadableFormat(resTime.medianResTimeSeconds),
      },
    ];
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
